import React from "react";
import { Check } from "@phosphor-icons/react";
import { MaskedHeading, Fade, FadeUp, ImageReveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { useContent } from "@/context/ContentContext";
import { telHref } from "@/lib/content";

// Conversion-focused landing template for Google Ads / META campaigns.
export default function LandingTemplate({ testId, eyebrow, titleLines, valueProp, image, points = [], gallery = [], page }) {
  const { contact } = useContent();
  return (
    <div data-testid={testId}>
      {/* Split hero with inline form */}
      <section className="relative pt-28 md:pt-0 md:min-h-[100svh] grid md:grid-cols-2">
        <div className="relative order-2 md:order-1 flex items-center px-6 md:px-16 py-16 md:py-24">
          <div className="w-full max-w-xl">
            <Fade>
              <p className="text-xs uppercase tracking-label text-accent mb-6">{eyebrow}</p>
            </Fade>
            <h1 className="font-display font-light leading-[0.95] text-5xl md:text-7xl tracking-tight">
              <MaskedHeading lines={titleLines} delay={0.2} />
            </h1>
            <FadeUp delay={0.25}>
              <p className="mt-6 text-muted-foreground font-light text-lg">{valueProp}</p>
            </FadeUp>
            <FadeUp delay={0.35}>
              <ul className="mt-8 space-y-3">
                {points.map((p) => (
                  <li key={p} className="flex items-start gap-3 font-light">
                    <Check size={20} weight="light" className="text-accent mt-1 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.45}>
              <div className="mt-10 border-t border-border pt-8">
                <p className="text-xs uppercase tracking-label text-muted-foreground mb-6">Book et uforpligtende inspirationsmøde</p>
                <LeadForm source="Google Ads" page={page} compact />
                <a href={telHref(contact.phone)} className="inline-block mt-6 text-sm font-light" data-testid="landing-phone">
                  Eller ring: <span className="font-display text-2xl">{contact.phone}</span>
                </a>
              </div>
            </FadeUp>
          </div>
        </div>

        <div className="order-1 md:order-2 relative min-h-[42svh] md:min-h-0">
          <div className="absolute inset-0">
            <img src={image} alt={titleLines.join(" ")} className="img-cover" />
          </div>
          <div className="absolute inset-0 bg-black/10" />
        </div>
      </section>

      {/* Proof gallery */}
      {gallery.length > 0 && (
        <section className="bg-foreground text-background py-20 md:py-28">
          <div className="mx-auto max-w-[1600px] px-6 md:px-12">
            <FadeUp>
              <p className="text-xs uppercase tracking-label text-background/50 mb-10">Udført af Hagelund</p>
            </FadeUp>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {gallery.map((g, i) => (
                <ImageReveal key={g} src={g} alt="Reference" className="aspect-[4/5]" delay={(i % 3) * 0.08} />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
